import React, {
  useMemo,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import { HorizontalFilterBarItemProps } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { useFilterStore } from 'apps/sigma2-ui/src/filters-v2/lib/useFilterStore';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { checkShouldApplyGlobalFilter } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineCleanedGlobalFiltersGivenUpdatedFilterValue } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineGlobalFilterDefinitions } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineGlobalFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineGlobalFilterStoreScope } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getGlobalFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyGlobalFn } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnTryInitializeSecondaryFilterFn } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { setGlobalFilters } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { tryInitializeGlobalFilters } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import usePrevious from 'apps/sigma2-ui/src/lib/hooks/usePrevious';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { determineGlobalFilterBarItems } from 'apps/sigma2-ui/src/filters-v2/lib/determineGlobalFilterBarItems';
import { SetterWithReducer } from 'libs/end-to-end/src/ui-types';
import { DraftDashboard } from 'libs/end-to-end/src/dashboard';
import { UpdateDashboardDraftGlobalFiltersFn } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { useEmbedMode } from 'apps/sigma2-ui/src/components/EmbedModeContext';

export interface UseGlobalFiltersHook {
  /**
   * The items for the global filter bar
   */
  items: HorizontalFilterBarItemProps[];

  /**
   * The current secondary filter selections.
   */
  globalFilterSelections: SecondaryFilterSelection[];

  /**
   * Set the filters from outside the filter bar. When using this method, your onApply callback will be called, however you will get a boolean value indicating it was a programmatic change.
   * @param params
   */
  setFilters: (params: {
    globalFilterSelections: SecondaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;

  /**
   * Reset the filters to their initial state.
   * @see useOnUserAnalysisCancelCallback
   */
  resetFiltersToInitialState: (selection?: PrimaryFilterSelection) => void;

  /**
   * Indicates whether the filter store has been initialized.
   */
  isInitialized: boolean;

  manualApply: () => void;
}

export const useGlobalFilters = ({
  onApply,
  disabled,
  onTryInitializeGlobalFilter,
  scopeId,
}: {
  onApply?: OnApplyGlobalFn;
  disabled?: boolean;
  onTryInitializeGlobalFilter?: OnTryInitializeSecondaryFilterFn;
  scopeId: string;
}): UseGlobalFiltersHook => {
  const store = useFilterStore();

  const language = useLanguage();
  const { myObservation } = useObservation();
  const { myUser } = useUser();
  const isEmbedMode = useEmbedMode();

  const { organizationId, databaseId, databaseRevisionId } = useMemo(() => {
    return {
      databaseId: myObservation?.type.databaseId ?? '',
      databaseRevisionId: myObservation?.type.databaseRevisionId ?? '',
      organizationId: myUser?.organization?.id ?? '',
    };
  }, [myObservation, myUser]);

  const [stagingGlobalFilters, setStagingGlobalFilters] = useState<
    SecondaryFilterSelection[]
  >([]);

  const [selectedGlobalFilterKey, setSelectedGlobalFilterKey] = useState<
    string | null
  >(null);

  const globalScope = useMemo(() => {
    return determineGlobalFilterStoreScope({
      databaseId,
      databaseRevisionId,
      organizationId,
      scopeId,
    });
  }, [databaseId, databaseRevisionId, organizationId, scopeId]);

  const globalFilterDefinitions = useMemo(() => {
    return determineGlobalFilterDefinitions({
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
    });
  }, [myObservation?.filterSetDefinitionsWithValues]);

  const resetStagingGlobalFilters = useCallback(() => {
    const globalFilterSelections = determineGlobalFilterSelections({
      myObservation,
      store,
      organizationId,
      databaseId,
      databaseRevisionId,
      scopeId,
    });
    setStagingGlobalFilters([...globalFilterSelections]);
  }, [
    databaseId,
    databaseRevisionId,
    myObservation,
    organizationId,
    scopeId,
    store,
  ]);

  const setStagingGlobalFilter = useCallback(
    ({
      value,
      secondaryFilterKey: filterKey,
    }: {
      secondaryFilterKey: string;
      value: FilterStoreValue;
    }) => {
      setStagingGlobalFilters((prev) =>
        determineCleanedGlobalFiltersGivenUpdatedFilterValue({
          prev,
          value,
          filterKey,
          myObservation,
        }),
      );
    },
    [myObservation],
  );

  const onTryInitializeGlobalFilterCb: OnTryInitializeSecondaryFilterFn =
    useCallback(
      ({ defaultValue, secondaryFilterKey }) => {
        if (onTryInitializeGlobalFilter) {
          return onTryInitializeGlobalFilter({
            secondaryFilterKey,
            defaultValue,
          });
        }
        return defaultValue;
      },
      [onTryInitializeGlobalFilter],
    );

  // Check if all global filters are initialized, which lets the world know we are ready to go.
  const isInitialized = useMemo(() => {
    return (
      Boolean(myObservation) &&
      globalFilterDefinitions.every((globalFilterDefinition) => {
        const fullKey = getGlobalFilterFullKey({
          filterKey: globalFilterDefinition.columnHeaderValue,
        });
        return (
          globalScope &&
          store.isInitialized({ scope: globalScope, key: fullKey })
        );
      })
    );
  }, [globalFilterDefinitions, globalScope, myObservation, store]);

  const resetFiltersToInitialStateCb = useCallback(() => {
    const globalFilterDefinitions = determineGlobalFilterDefinitions({
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
    });

    const globalFilterSelections: SecondaryFilterSelection[] = [];

    for (const secondaryFilter of globalFilterDefinitions) {
      const fullKey = getGlobalFilterFullKey({
        filterKey: secondaryFilter.columnHeaderValue,
      });

      if (
        !globalScope ||
        !store.isInitialized({
          scope: globalScope,
          key: fullKey,
        })
      ) {
        continue;
      }

      globalFilterSelections.push({
        value: store.getInitialValue({
          key: fullKey,
          scope: globalScope,
        }),
        secondaryFilterKey: secondaryFilter.columnHeaderValue,
      });
    }

    setGlobalFilters({
      store,
      organizationId,
      databaseRevisionId,
      databaseId,
      myObservation,
      selections: globalFilterSelections,
      scopeId,
    });
  }, [
    myObservation,
    store,
    organizationId,
    databaseRevisionId,
    databaseId,
    scopeId,
    globalScope,
  ]);

  // Any time primaryFilterKeys or secondaryFilterKeys is updated, we should try to initialize all our filters.
  const tryInitializeFiltersCb = useCallback(() => {
    return tryInitializeGlobalFilters({
      databaseId,
      databaseRevisionId,
      organizationId,
      store,
      myObservation,
      onTryInitializeGlobalFilter: onTryInitializeGlobalFilterCb,
      scopeId,
    });
  }, [
    databaseId,
    databaseRevisionId,
    organizationId,
    store,
    myObservation,
    onTryInitializeGlobalFilterCb,
    scopeId,
  ]);

  useEffect(() => {
    tryInitializeFiltersCb();
  }, [tryInitializeFiltersCb]);

  const globalFilterSelections = useMemo(() => {
    return determineGlobalFilterSelections({
      store,
      organizationId,
      databaseId,
      databaseRevisionId,
      myObservation,
      scopeId,
    });
  }, [
    databaseId,
    databaseRevisionId,
    myObservation,
    organizationId,
    scopeId,
    store,
  ]);

  // Call onApply when primary or secondary filters change
  const programmaticChangeRef = useRef(false);
  const isInitialApplyRef = useRef(true);
  const shouldPreventAutoSubmitRef = useRef(false);

  const previousGlobalFilterSelections = usePrevious(globalFilterSelections);

  useEffect(() => {
    const shouldApplyFilter = checkShouldApplyGlobalFilter({
      globalFilterSelections,
      previousGlobalFilterSelections,
      shouldPreventAutoSubmit: shouldPreventAutoSubmitRef.current,
    });

    shouldPreventAutoSubmitRef.current = false;

    if (onApply && shouldApplyFilter) {
      const isProgrammaticChange = programmaticChangeRef.current;
      programmaticChangeRef.current = false;

      const isInitialApply = isInitialApplyRef.current;
      isInitialApplyRef.current = false;

      onApply({
        globalFilterSelections,
        isProgrammaticChange,
        isInitialApply,
      });
    } else {
      programmaticChangeRef.current = false;
    }
  }, [onApply, previousGlobalFilterSelections, globalFilterSelections]);

  const setFiltersCb = useCallback(
    ({
      globalFilterSelections,
      shouldPreventAutoSubmit,
    }: {
      globalFilterSelections: SecondaryFilterSelection[];
      shouldPreventAutoSubmit?: boolean;
    }) => {
      programmaticChangeRef.current = true;
      if (shouldPreventAutoSubmit) {
        // Mark our next useEffect to be aware that our user preference is to supersede the preventAutoSubmit feature.
        shouldPreventAutoSubmitRef.current = true;
      }

      setGlobalFilters({
        selections: globalFilterSelections,
        databaseId,
        databaseRevisionId,
        store,
        organizationId,
        scopeId,
        myObservation,
      });
    },
    [
      databaseId,
      databaseRevisionId,
      store,
      organizationId,
      scopeId,
      myObservation,
    ],
  );

  const setGlobalFiltersCb = useCallback(
    ({
      globalFilterSelections,
      shouldPreventAutoSubmit,
    }: {
      globalFilterSelections: SecondaryFilterSelection[];
      shouldPreventAutoSubmit?: boolean;
    }) => {
      if (shouldPreventAutoSubmit) {
        // Mark our next useEffect to be aware that our user preference is to supersede the preventAutoSubmit feature.
        shouldPreventAutoSubmitRef.current = true;
      }

      setGlobalFilters({
        selections: globalFilterSelections,
        databaseId,
        databaseRevisionId,
        store,
        organizationId,
        scopeId,
        myObservation,
      });
    },
    [
      databaseId,
      databaseRevisionId,
      store,
      organizationId,
      scopeId,
      myObservation,
    ],
  );

  const manualApply = useCallback(() => {
    if (!onApply) {
      return;
    }
    onApply({
      globalFilterSelections,
      isProgrammaticChange: false,
      isInitialApply: false,
    });
  }, [onApply, globalFilterSelections]);

  return useMemo(() => {
    return {
      items: determineGlobalFilterBarItems({
        databaseId,
        databaseRevisionId,
        store,
        organizationId,
        resetStagingGlobalFilters,
        stagingGlobalFilters,
        setStagingGlobalFilter,
        setGlobalFilters: setGlobalFiltersCb,
        myObservation,
        selectedGlobalFilterKey,
        setSelectedGlobalFilterKey,
        disabled: Boolean(disabled),
        scopeId,
        language,
        isEmbedMode,
      }),
      globalFilterSelections,
      setFilters: setFiltersCb,
      resetFiltersToInitialState: resetFiltersToInitialStateCb,
      isInitialized,
      manualApply,
    };
  }, [
    databaseId,
    databaseRevisionId,
    store,
    organizationId,
    resetStagingGlobalFilters,
    stagingGlobalFilters,
    setStagingGlobalFilter,
    setFiltersCb,
    setGlobalFiltersCb,
    myObservation,
    resetFiltersToInitialStateCb,
    selectedGlobalFilterKey,
    disabled,
    scopeId,
    language,
    isEmbedMode,
    globalFilterSelections,
    isInitialized,
    manualApply,
  ]);
};

export const useDashboardGlobalFilters = ({
  dashboard,
  updateDashboardDraftGlobalFilters,
  dashboardId,
}: {
  dashboardId: string;
  dashboard: DraftDashboard | null;
  updateDashboardDraftGlobalFilters: UpdateDashboardDraftGlobalFiltersFn;
}) => {
  const existingGlobalFilters = useMemo(() => {
    return dashboard?.globalFilters ?? null;
  }, [dashboard]);

  const onGlobalApplyFn: OnApplyGlobalFn = useCallback(
    (params) => {
      console.log('Dashboard global filters onApply called:', params);
      // Do not update if this was a programmatic change.
      if (params.isProgrammaticChange || params.isInitialApply) {
        return;
      }

      const newThing = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections: params.globalFilterSelections,
      }).filter((f) => f.values.length > 0);
      if (newThing.length === 0 && existingGlobalFilters === null) {
        return;
      }
      updateDashboardDraftGlobalFilters(newThing);
    },
    [existingGlobalFilters, updateDashboardDraftGlobalFilters],
  );

  const onTryInitializeGlobalFilterCb: OnTryInitializeSecondaryFilterFn =
    useCallback(
      (params) => {
        if (!dashboard) {
          return null;
        }

        const globalFilter = dashboard.globalFilters?.find(
          (f) => f.columnHeaderValue === params.secondaryFilterKey,
        );

        if (!globalFilter) {
          return params.defaultValue;
        }

        return globalFilter.values;
      },
      [dashboard],
    );

  return useGlobalFilters({
    scopeId: `dashboard-details/${dashboardId || ''}`,
    disabled: false,
    onApply: onGlobalApplyFn,
    onTryInitializeGlobalFilter: onTryInitializeGlobalFilterCb,
  });
};
